import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Image, Upload, Sparkles, FolderOpen, X, Loader2, 
  RefreshCw, Check, AlertCircle, Trash2, Search,
  CheckCircle, ImagePlus
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Progress } from '../ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui/tabs';
import { toast } from 'sonner';
import { API_BASE } from '../../config/api';
import { getThumbnailUrl } from '../../lib/utils';

const API = API_BASE;

// Maximum file size: 5MB
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

export default function CourseImageManager({ 
  currentImageUrl, 
  onImageSelect, 
  courseTitle = '', 
  courseDescription = '',
  token 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('upload');
  
  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // AI Generation state
  const [generating, setGenerating] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [generatedImage, setGeneratedImage] = useState(null);
  
  // Preview state - a broken thumbnail URL must not tear out the DOM React owns
  const [previewFailed, setPreviewFailed] = useState(false);

  // Media Library state
  const [mediaItems, setMediaItems] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [mediaSearch, setMediaSearch] = useState('');
  
  const fileInputRef = useRef(null);

  // Fetch media library when tab changes to library
  useEffect(() => {
    if (activeTab === 'library' && isOpen) {
      fetchMediaLibrary();
    }
  }, [activeTab, isOpen]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setPreviewUrl(null);
      setSelectedFile(null);
      setUploadError(null);
      setUploadProgress(0);
      setGeneratedImage(null);
      setAiPrompt('');
    }
  }, [isOpen]);

  const fetchMediaLibrary = async () => {
    setLoadingMedia(true);
    try {
      const res = await axios.get(`${API}/api/media`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to only show images
      const images = (res.data || []).filter(m => m.type === 'image');
      setMediaItems(images);
    } catch (err) {
      console.error('Failed to fetch media:', err);
      toast.error('Failed to load media library');
    } finally {
      setLoadingMedia(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError(null);

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setUploadError('Invalid file type. Please upload a JPG, PNG, GIF, or WebP image.');
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setUploadError(`File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB.`);
      return;
    }

    setSelectedFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Upload file
  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setUploadProgress(0);
    setUploadError(null);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('file_type', 'image');

    try {
      const res = await axios.post(`${API}/api/media/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      // Get the URL from response
      const imageUrl = res.data.url.startsWith('/') 
        ? `${API}${res.data.url}` 
        : res.data.url;
      
      // Add cache-busting parameter to force image refresh
      const finalUrl = imageUrl.includes('?') 
        ? `${imageUrl}&_t=${Date.now()}`
        : `${imageUrl}?_t=${Date.now()}`;

      toast.success('Image uploaded successfully!');
      onImageSelect(finalUrl);
      setIsOpen(false);
    } catch (err) {
      console.error('Upload failed:', err);
      setUploadError(err.response?.data?.detail || 'Upload failed. Please try again.');
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Generate AI image
  const handleGenerateAI = async () => {
    setGenerating(true);
    setGeneratedImage(null);

    try {
      const promptToUse = aiPrompt.trim() || `Professional course thumbnail for: ${courseTitle}. ${courseDescription}`;
      
      const res = await axios.post(
        `${API}/api/admin/generate-thumbnail`,
        { 
          title: courseTitle || 'Course',
          description: promptToUse
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.thumbnail_url) {
        const imageUrl = res.data.thumbnail_url.startsWith('/')
          ? `${API}${res.data.thumbnail_url}`
          : res.data.thumbnail_url;
        setGeneratedImage(imageUrl);
        toast.success('Image generated successfully!');
      }
    } catch (err) {
      console.error('AI generation failed:', err);
      // Handle error - detail might be string or object
      const errorDetail = err.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' 
        ? errorDetail 
        : (errorDetail?.msg || 'Failed to generate image');
      toast.error(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  // Use generated image
  const handleUseGeneratedImage = () => {
    if (generatedImage) {
      onImageSelect(generatedImage);
      setIsOpen(false);
    }
  };

  // Select from media library
  const handleSelectFromLibrary = (item) => {
    const imageUrl = item.url.startsWith('/') 
      ? `${API}${item.url}` 
      : item.url;
    onImageSelect(imageUrl);
    setIsOpen(false);
    toast.success('Image selected from library');
  };

  // Filter media items by search
  const filteredMedia = mediaItems.filter(item =>
    item.filename?.toLowerCase().includes(mediaSearch.toLowerCase())
  );

  // A new URL deserves a fresh attempt - otherwise one broken image leaves
  // the error card showing for every image chosen afterwards.
  useEffect(() => {
    setPreviewFailed(false);
  }, [currentImageUrl]);

  // Remove current image
  const handleRemoveImage = () => {
    onImageSelect('');
  };

  return (
    <div className="space-y-3">
      <Label className="text-xs text-zinc-400">Course Thumbnail</Label>
      
      {/* Current Image Preview */}
      <div className="relative" key={currentImageUrl || 'no-image'}>
        {currentImageUrl ? (
          <div className="relative group">
            {previewFailed ? (
              <button
                type="button"
                onClick={() => setIsOpen(true)}
                className="w-full h-40 rounded-lg border border-red-500/50 bg-red-500/10 flex flex-col items-center justify-center gap-2"
              >
                <AlertCircle className="h-8 w-8 text-red-400" />
                <span className="text-xs text-red-400">Image failed to load</span>
                <span className="text-xs text-zinc-500">Click to select a new image</span>
              </button>
            ) : (
            <img
              key={currentImageUrl}
              src={getThumbnailUrl(currentImageUrl)}
              alt="Course thumbnail"
              className="w-full h-40 object-cover rounded-lg border border-zinc-700"
              onError={(e) => {
                console.error('Failed to load thumbnail:', e.target.src);
                setPreviewFailed(true);
              }}
            />
            )}
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setIsOpen(true)}
                className="bg-white/20 hover:bg-white/30 text-white"
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                Change
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={handleRemoveImage}
                className="bg-red-500/20 hover:bg-red-500/30 text-white"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            {!previewFailed && (
              <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                <CheckCircle className="h-3 w-3 text-green-400" />
                Image Selected
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={() => setIsOpen(true)}
            className="w-full h-40 border-2 border-dashed border-zinc-700 rounded-lg flex flex-col items-center justify-center gap-3 hover:border-navigate-gold/50 hover:bg-zinc-800/30 transition-colors"
          >
            <div className="w-12 h-12 bg-zinc-800 rounded-full flex items-center justify-center">
              <ImagePlus className="h-6 w-6 text-zinc-500" />
            </div>
            <div className="text-center">
              <p className="text-sm text-zinc-400">Add Course Image</p>
              <p className="text-xs text-zinc-500">Generate with AI, upload, or select from library</p>
            </div>
          </button>
        )}
      </div>

      {/* Image Selection Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Image className="h-5 w-5 text-navigate-gold" />
              Course Thumbnail
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Choose how you want to add an image for your course
            </DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="grid grid-cols-3 bg-zinc-800 mb-4">
              <TabsTrigger value="upload" className="data-[state=active]:bg-navigate-gold data-[state=active]:text-black">
                <Upload className="h-4 w-4 mr-2" />
                Upload
              </TabsTrigger>
              <TabsTrigger value="generate" className="data-[state=active]:bg-navigate-gold data-[state=active]:text-black">
                <Sparkles className="h-4 w-4 mr-2" />
                Generate with AI
              </TabsTrigger>
              <TabsTrigger value="library" className="data-[state=active]:bg-navigate-gold data-[state=active]:text-black">
                <FolderOpen className="h-4 w-4 mr-2" />
                Media Library
              </TabsTrigger>
            </TabsList>

            {/* Upload Tab */}
            <TabsContent value="upload" className="flex-1 overflow-y-auto">
              <div className="space-y-4">
                {/* Drop Zone */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                    uploadError 
                      ? 'border-red-500/50 bg-red-500/5' 
                      : previewUrl 
                        ? 'border-green-500/50 bg-green-500/5'
                        : 'border-zinc-700 hover:border-navigate-gold/50 hover:bg-zinc-800/30'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  
                  {previewUrl ? (
                    <div className="space-y-3">
                      <img 
                        src={previewUrl} 
                        alt="Preview" 
                        className="max-h-48 mx-auto rounded-lg shadow-lg"
                      />
                      <p className="text-sm text-green-400 flex items-center justify-center gap-2">
                        <Check className="h-4 w-4" />
                        {selectedFile?.name}
                      </p>
                      <p className="text-xs text-zinc-500">Click to choose a different file</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="w-16 h-16 bg-zinc-800 rounded-full flex items-center justify-center mx-auto">
                        <Upload className="h-8 w-8 text-zinc-500" />
                      </div>
                      <div>
                        <p className="text-white font-medium">Click to upload</p>
                        <p className="text-sm text-zinc-500">or drag and drop</p>
                      </div>
                      <p className="text-xs text-zinc-600">
                        JPG, PNG, GIF or WebP (max 5MB)
                      </p>
                    </div>
                  )}
                </div>

                {/* Upload Error */}
                {uploadError && (
                  <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 p-3 rounded-lg">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    {uploadError}
                  </div>
                )}

                {/* Upload Progress */}
                {uploading && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-zinc-400">Uploading...</span>
                      <span className="text-navigate-gold">{uploadProgress}%</span>
                    </div>
                    <Progress value={uploadProgress} className="h-2" />
                  </div>
                )}

                {/* Upload Button */}
                {previewUrl && !uploading && (
                  <Button
                    onClick={handleUpload}
                    className="w-full bg-navigate-gold hover:bg-navigate-gold/90 text-black font-medium"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload & Use This Image
                  </Button>
                )}
              </div>
            </TabsContent>

            {/* AI Generation Tab */}
            <TabsContent value="generate" className="flex-1 overflow-y-auto">
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/20 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Sparkles className="h-5 w-5 text-purple-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-medium">Generate with NAVI</h4>
                      <p className="text-sm text-zinc-400 mt-1">
                        NAVI will create a professional thumbnail based on your course title and description. 
                        Optionally, add your own prompt for more control.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Custom Prompt */}
                <div className="space-y-2">
                  <Label className="text-zinc-300">Custom Prompt (Optional)</Label>
                  <Textarea
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder={`Leave empty to auto-generate based on:\n"${courseTitle || 'Your course title'}"`}
                    className="bg-zinc-800 border-zinc-700 min-h-[100px] text-sm"
                  />
                  <p className="text-xs text-zinc-500">
                    Describe the style, colors, or specific elements you want in the image
                  </p>
                </div>

                {/* Generate Button */}
                <Button
                  onClick={handleGenerateAI}
                  disabled={generating}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                >
                  {generating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generating... (10-20 seconds)
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Generate Image
                    </>
                  )}
                </Button>

                {/* Generated Image Preview */}
                {generatedImage && (
                  <div className="space-y-3">
                    <Label className="text-zinc-300">Generated Image</Label>
                    <div className="relative">
                      <img
                        src={generatedImage}
                        alt="AI Generated"
                        className="w-full rounded-lg border border-zinc-700 shadow-lg"
                      />
                      <div className="absolute top-2 right-2 bg-purple-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                        <Sparkles className="h-3 w-3" />
                        AI Generated
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleUseGeneratedImage}
                        className="flex-1 bg-navigate-gold hover:bg-navigate-gold/90 text-black"
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Use This Image
                      </Button>
                      <Button
                        onClick={handleGenerateAI}
                        variant="outline"
                        className="border-zinc-700"
                        disabled={generating}
                      >
                        <RefreshCw className={`h-4 w-4 ${generating ? 'animate-spin' : ''}`} />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Media Library Tab */}
            <TabsContent value="library" className="flex-1 overflow-hidden flex flex-col">
              <div className="space-y-4 flex-1 flex flex-col overflow-hidden">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                  <Input
                    value={mediaSearch}
                    onChange={(e) => setMediaSearch(e.target.value)}
                    placeholder="Search images..."
                    className="pl-10 bg-zinc-800 border-zinc-700"
                  />
                </div>

                {/* Media Grid */}
                <div className="flex-1 overflow-y-auto">
                  {loadingMedia ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-navigate-gold" />
                    </div>
                  ) : filteredMedia.length === 0 ? (
                    <div className="text-center py-12">
                      <FolderOpen className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
                      <p className="text-zinc-400">No images in library</p>
                      <p className="text-sm text-zinc-500 mt-1">
                        Upload images or generate with AI to build your library
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-3">
                      {filteredMedia.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => handleSelectFromLibrary(item)}
                          className="group relative aspect-video rounded-lg overflow-hidden border border-zinc-700 hover:border-navigate-gold transition-colors"
                        >
                          <img
                            src={item.url.startsWith('/') ? `${API}${item.url}` : item.url}
                            alt={item.filename}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              // No external placeholder service - enterprise
                              // networks block it and it leaks the request.
                              e.target.style.visibility = 'hidden';
                            }}
                          />
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <div className="bg-navigate-gold text-black px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1">
                              <Check className="h-4 w-4" />
                              Select
                            </div>
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                            <p className="text-xs text-white truncate">{item.filename}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Upload to Library Button */}
                <div className="border-t border-zinc-800 pt-4">
                  <Button
                    variant="outline"
                    className="w-full border-zinc-700"
                    onClick={() => setActiveTab('upload')}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload New Image to Library
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
